'use client';

import Link from 'next/link';
import { useState } from 'react';

import type { HomeContent } from '@/entities/content';
import { primaryMedia, productHref, ProductMedia, type Product } from '@/entities/product';
import { formatMoney } from '@/shared/lib/money';
import { MediaTag } from '@/shared/ui/badge';
import { ButtonLink } from '@/shared/ui/button';
import { Icon } from '@/shared/ui/icon';
import { MediaFrame } from '@/shared/ui/media-frame';

import styles from './morning-banner.module.scss';

type Spot = HomeContent['morning']['hotspots'][number] & { product: Product };

/**
 * "Утро для себя": photo with product hotspots (design 01B). The first card is open, hover or focus opens another.
 * Phones get numbered markers and a list under the photo (designer note: cards do not fit a narrow screen).
 */
export function MorningBanner({
  morning,
  products,
  showDemoNotes,
}: {
  morning: HomeContent['morning'];
  products: Product[];
  showDemoNotes: boolean;
}) {
  const spots: Spot[] = morning.hotspots.flatMap((spot) => {
    const product = products.find((item) => item.slug === spot.productSlug);
    return product ? [{ ...spot, product }] : [];
  });
  const [active, setActive] = useState(0);
  return (
    <section className={styles.root} aria-labelledby="morning-title">
      <div className={styles.stage}>
        <div className={styles.photo}>
          <MediaFrame
            src={morning.image.src}
            alt={morning.image.alt}
            position={morning.image.position}
            sizes="100vw"
            className={styles.fill}
          />
        </div>
        <div className={styles.veil} />
        <div className={styles.content}>
          <h2 id="morning-title" className={styles.title}>
            Утро для себя
          </h2>
          <ButtonLink href="/catalog?direction=body" variant="light">
            Смотреть подборку <Icon name="arrow-right" size={16} strokeWidth={1.4} />
          </ButtonLink>
        </div>
        {spots.map((spot, index) => {
          const style = { '--spot-x': `${spot.x}%`, '--spot-y': `${spot.y}%` };
          const open = index === active;
          const price = spot.product.price ? formatMoney(spot.product.price) : null;
          return (
            <div
              key={spot.productSlug}
              className={styles.spot}
              style={style}
              data-open={open || undefined}
              data-placement={spot.placement}
              onMouseEnter={() => setActive(index)}
              onFocus={() => setActive(index)}
            >
              <Link
                href={productHref(spot.product)}
                className={styles.dot}
                aria-label={price ? `${spot.product.name}, ${price}` : spot.product.name}
              >
                <Icon name="plus" size={14} strokeWidth={1.6} className={styles.plus} />
                <span className={styles.number} aria-hidden="true">
                  {index + 1}
                </span>
              </Link>
              <div className={styles.card}>
                <ProductMedia
                  media={primaryMedia(spot.product)}
                  fallbackLabel={spot.product.shortLabel}
                  sizes="232px"
                  className={styles.thumb}
                  placeholderStyle="plain"
                />
                <span className={styles.name}>{spot.product.name}</span>
                {price && <span className={styles.price}>{price}</span>}
                <ButtonLink href={productHref(spot.product)} fullWidth tabIndex={open ? 0 : -1}>
                  Смотреть товар
                </ButtonLink>
              </div>
            </div>
          );
        })}
        {showDemoNotes && morning.image.demoTag && <MediaTag>{morning.image.demoTag}</MediaTag>}
      </div>
      <ol className={styles.list} aria-label="Товары на фото">
        {spots.map((spot, index) => (
          <li key={spot.productSlug}>
            <Link href={productHref(spot.product)} className={styles.item}>
              <span className={styles.index}>{index + 1}</span>
              <span className={styles.label}>{spot.product.name}</span>
              {spot.product.price && (
                <span className={styles.cost}>{formatMoney(spot.product.price)}</span>
              )}
            </Link>
          </li>
        ))}
      </ol>
    </section>
  );
}
